import React, { useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "~/components/Button";
import { Input } from "~/components/Controls/Input";
import { SelectInput } from "~/components/Controls/Select";
import { useConfirm, useMessage } from "~/components/Dialogs";
import { IntentText } from "~/components/Form";
import { Progress } from "~/components/Progress";
import { TableRowActions, TableSort } from "~/components/Table";
import { Intent } from "~/lib/intent";
import { sortByString } from "~/lib/sort";
import {
  createExternalBuyer,
  deleteExternalBuyer,
  updateExternalBuyer,
} from "~/modules/Dictionary/api";
import { DictionaryUsageLinks } from "~/modules/Dictionary/components/DictionaryUsageLinks";
import { IExternalBuyer } from "~/modules/Dictionary/models/ExternalBuyer";
import { EXTERNAL_BUYERS_QUERY_KEY, useExternalBuyersQuery } from "~/modules/Dictionary/queries";
import { clientsLinks } from "~/modules/Clients/links";
import style from "./ExternalBuyersTab.module.scss";

const getErrorMessage = (error: any): string => error?.response?.data?.message || error?.message || "Unknown error";

type FormMode = "idle" | "create" | "edit";

type UpdatePayload = {
  id: number;
  name: string;
  externalBuyerId: string | null;
};

export const ExternalBuyersTab = () => {
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const message = useMessage();

  const { data: items = [], isPending, isFetching, isError, error, refetch } = useExternalBuyersQuery();

  const [mode, setMode] = useState<FormMode>("idle");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [name, setName] = useState("");
  const [externalBuyerId, setExternalBuyerId] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [agencyFilterId, setAgencyFilterId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  const createMutation = useMutation({
    mutationFn: (payload: { name: string; externalBuyerId: string | null }) => createExternalBuyer(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: EXTERNAL_BUYERS_QUERY_KEY });
      setName("");
      setExternalBuyerId("");
      setFormError(null);
      setEditingId(null);
      setMode("idle");
    },
  });

  const updateMutation = useMutation({
    mutationFn: (payload: UpdatePayload) =>
      updateExternalBuyer(payload.id, { name: payload.name, externalBuyerId: payload.externalBuyerId }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: EXTERNAL_BUYERS_QUERY_KEY });
      setName("");
      setExternalBuyerId("");
      setFormError(null);
      setEditingId(null);
      setMode("idle");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteExternalBuyer(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: EXTERNAL_BUYERS_QUERY_KEY });
    },
  });

  const normalizedItems = useMemo(() => items ?? [], [items]);
  const agencyItems = useMemo(() => {
    const map = new Map<number, string>();
    normalizedItems.forEach((item) => {
      if (item.agencyId && item.agencyName) {
        map.set(item.agencyId, item.agencyName);
      }
    });
    return [...map.entries()]
      .map(([id, label]) => ({ value: id, label: `${id} · ${label}` }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [normalizedItems]);
  const filteredItems = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return normalizedItems.filter((item) => {
      if (agencyFilterId !== null && item.agencyId !== agencyFilterId) {
        return false;
      }
      if (!query) {
        return true;
      }
      const nameValue = (item.name ?? "").toLowerCase();
      const externalIdValue = (item.externalBuyerId ?? "").toLowerCase();
      return nameValue.includes(query) || externalIdValue.includes(query);
    });
  }, [normalizedItems, agencyFilterId, searchQuery]);
  const isBusy = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;
  const isFormOpen = mode !== "idle";
  const isEditing = mode === "edit";
  const canSubmit = name.trim().length > 0;

  const handleOpenAdd = () => {
    setFormError(null);
    setName("");
    setExternalBuyerId("");
    setEditingId(null);
    setMode("create");
  };

  const handleEdit = (item: IExternalBuyer) => {
    setFormError(null);
    setName(item.name ?? "");
    setExternalBuyerId(item.externalBuyerId ?? "");
    setEditingId(item.id);
    setMode("edit");
  };

  const handleCancel = () => {
    setFormError(null);
    setName("");
    setExternalBuyerId("");
    setEditingId(null);
    setMode("idle");
  };

  const handleSave = () => {
    setFormError(null);
    const trimmedName = name.trim();
    const trimmedExternalId = externalBuyerId.trim();
    const payloadExternalId = trimmedExternalId.length ? trimmedExternalId : null;
    if (!trimmedName) {
      return;
    }

    if (isEditing && editingId !== null) {
      updateMutation.mutate(
        { id: editingId, name: trimmedName, externalBuyerId: payloadExternalId },
        {
          onError: (e: any) => {
            const msg = getErrorMessage(e);
            setFormError(msg);
            message(msg, { intent: Intent.DANGER, icon: <i className="fa fa-times" /> });
          },
        }
      );
      return;
    }

    createMutation.mutate(
      { name: trimmedName, externalBuyerId: payloadExternalId },
      {
        onError: (e: any) => {
          const msg = getErrorMessage(e);
          setFormError(msg);
          message(msg, { intent: Intent.DANGER, icon: <i className="fa fa-times" /> });
        },
      }
    );
  };

  const handleDelete = (item: IExternalBuyer) =>
    confirm(`Delete external buyer "${item.name}"?`, {
      icon: <i className="fa fa-question" />,
      intent: Intent.DANGER,
      onOk: () => {
        deleteMutation.mutate(item.id, {
          onError: (e: any) => {
            message(getErrorMessage(e), { intent: Intent.DANGER, icon: <i className="fa fa-times" /> });
          },
        });
      },
    });

  return (
    <div>
      <div className={style.toolbar}>
        <div className={style.toolbarLeft}>
          {isFormOpen && (
            <div className={style.formRowTwo}>
              <Input
                className={style.field}
                placeholder="AgencyId_BuyerId"
                value={externalBuyerId}
                onChange={setExternalBuyerId}
                disabled={isBusy}
                autoFocus
              />
              <Input
                className={style.field}
                placeholder="Name"
                value={name}
                onChange={setName}
                disabled={isBusy}
              />
            </div>
          )}
          {!isFormOpen && (
            <div className={style.usageNote}>
              <div className={style.usageText}>
                <DictionaryUsageLinks
                  links={[
                    {
                      label: "Pivot",
                      href: "https://pivot.bidmachine.io/pivot/i/9df4681d23a3991fcb/2_Impressions",
                      external: true,
                    },
                  ]}
                />
                <div className={style.usageHint}>External Id is AgencyId_BuyerId.</div>
              </div>
              <div className={style.usageControls}>
                {!!agencyItems.length && (
                  <SelectInput
                    className={style.usageFilter}
                    items={agencyItems}
                    value={agencyFilterId}
                    onChange={setAgencyFilterId}
                    placeholder="Filter by agency"
                    clearable
                    searchable
                  />
                )}
                <Input
                  className={style.usageSearch}
                  placeholder="Search by External Id or Name"
                  value={searchQuery}
                  onChange={setSearchQuery}
                  rightIcon={<i className="fa fa-search input-search-icon" />}
                />
              </div>
            </div>
          )}
        </div>
        <div className={style.toolbarRight}>
          {!isFormOpen ? (
            <Button
              responsive
              intent={Intent.PRIMARY}
              disabled={isBusy}
              onClick={handleOpenAdd}
              leftIcon={<i className="fa fa-plus" />}
            >
              Add
            </Button>
          ) : (
            <>
              <Button
                responsive
                intent={Intent.PRIMARY}
                disabled={!canSubmit || isBusy}
                onClick={handleSave}
                leftIcon={<i className="fa fa-check" />}
              >
                {isEditing ? "Update" : "Save"}
              </Button>
              <Button
                responsive
                disabled={isBusy}
                onClick={handleCancel}
                leftIcon={<i className="fa fa-times" />}
              >
                Cancel
              </Button>
            </>
          )}
          <Button
            responsive
            intent={Intent.SUCCESS}
            disabled={isFetching}
            onClick={() => refetch()}
            leftIcon={<i className="fa fa-refresh" />}
          >
            Reload
          </Button>
        </div>
      </div>

      {isFormOpen && formError && <IntentText className={style.error} intent={Intent.DANGER}>{formError}</IntentText>}
      {isError && <IntentText className={style.error} intent={Intent.DANGER}>{getErrorMessage(error)}</IntentText>}

      <div className={style.tableWrap}>
        {isPending && filteredItems.length === 0 && <Progress message="Loading..." />}
        {!isPending && filteredItems.length === 0 && (
          <div className={style.emptyState}>No external buyers.</div>
        )}
        {filteredItems.length > 0 && (
          <TableSort
            records={filteredItems}
            header
            columns={[
              { header: "External Id", field: "externalBuyerId", sort: sortByString },
              { header: "Name", field: "name", sort: sortByString },
              {
                header: "Agency",
                field: "agencyName",
                sort: sortByString,
                cellAttrs: { className: style.agencyCell },
                cell: (item: IExternalBuyer) =>
                  item.agencyId && item.agencyName ? (
                    <NavLink to={clientsLinks.agency(item.agencyId)}>
                      {item.agencyId} · {item.agencyName}
                    </NavLink>
                  ) : (
                    item.agencyName ?? (item.agencyId ? String(item.agencyId) : "—")
                  ),
              },
              {
                short: true,
                cell: (item: IExternalBuyer) => (
                  <TableRowActions>
                    <Button
                      small
                      variant="menu"
                      intent={Intent.PRIMARY}
                      disabled={isBusy}
                      onClick={() => handleEdit(item)}
                      leftIcon={<i className="fa fa-edit" />}
                    >
                      Edit
                    </Button>
                    <Button
                      small
                      variant="menu"
                      intent={Intent.DANGER}
                      disabled={isBusy}
                      onClick={() => handleDelete(item)}
                      leftIcon={<i className="fa fa-trash" />}
                    >
                      Delete
                    </Button>
                  </TableRowActions>
                ),
              },
            ]}
          />
        )}
      </div>
    </div>
  );
};
